import React, { useCallback } from 'react';
import { useTranslation } from 'react-i18next';
import { useNavigate } from 'react-router-dom';
import {
  QAlert,
  QBadge,
  QBox,
  QButton,
  QFlex,
  QHeading,
  QOpenPropertiesPanelButton,
  QSpacer,
  QStack,
  QText,
  useCurrentUser,
} from '@qualio/ui-components';
import { useExport, ExportFormats } from '@qualio/export-components';
import * as DisplayStrings from '../../displayStrings';
import { exportApi } from '../../api';
import { StatusTag } from '../../components';
import KebabMenu from './components/KebabMenu/KebabMenu';
import { EventStatus } from '../../types';
import { useEventPermissions } from '../../hooks';
import { useLocalisedFormatDateTime } from '../../utils/datetimeUtils';
import { toAbsoluteUrl } from '../../utils/UrlUtils';
import { DateTime } from 'luxon';

type QualityEventHeaderProps = {
  code: string;
  title: string;
  status: EventStatus;
  due: string;
  updated_at: number;
  description: string;
  onEditEventClicked: () => void;
  onChangeOwnerClicked: () => void;
  onEscalateEventClicked: () => void;
  onReopenClicked: () => void;
  onRejectClicked: () => void;
  onAcceptClicked: () => void;
  onOpenClicked: () => void;
  onCloseEventClicked: () => void;
  onCancelEventClicked: () => void;
  onUpdateEventPropertiesClicked: () => void;
  eventId: number;
  companyId: number;
  userTZ: string;
  alertFetchingRelatedContent: boolean;
};

const QualityEventHeader: React.FC<QualityEventHeaderProps> = ({
  code,
  title,
  status,
  due,
  updated_at,
  description,
  onEditEventClicked,
  onChangeOwnerClicked,
  onEscalateEventClicked,
  onReopenClicked,
  onRejectClicked,
  onAcceptClicked,
  onOpenClicked,
  onCloseEventClicked,
  onCancelEventClicked,
  onUpdateEventPropertiesClicked,
  eventId,
  companyId,
  alertFetchingRelatedContent,
}) => {
  const { t } = useTranslation();
  const navigate = useNavigate();
  const formatDateTime = useLocalisedFormatDateTime();

  const { trigger: handleTriggerExport, isLoading: isExportLoading } = useExport(exportApi, {
    toastTitle: t('common.notifications.exportingEventCodeTitle', { code, title }),
  });

  const handleExport = useCallback(
    () =>
      handleTriggerExport({
        exports: [
          {
            format: ExportFormats.medtech,
            filename: `${DateTime.now().toFormat('ddMMyy')}-${code}-export`,
            export_url: `${process.env.REACT_APP_MEDTECH_BACKEND_API_URL}/v2/${companyId}/issues/${eventId}/export`,
          },
        ],
        metadata: {
          domain: 'event',
          resource_name: `${code} ${title} export`,
        },
      }),
    [code, companyId, eventId, handleTriggerExport, title],
  );

  const { formatDate } = useCurrentUser();

  const { canModifyEvent } = useEventPermissions();
  const showRejectAndAcceptButton = canModifyEvent && status === 'imported';

  return (
    <QBox>
      <QFlex>
        <QBox lineHeight="32px">
          <QButton variant="link" onClick={() => navigate('/')} leftIcon="ArrowLeft">
            {DisplayStrings.Back}
          </QButton>
        </QBox>
        <QSpacer />
        <QOpenPropertiesPanelButton />
      </QFlex>
      <QBadge mt={4} textTransform="none" copyValue={toAbsoluteUrl(`/quality-events/events/${eventId}`)}>
        {code}
      </QBadge>
      <QFlex justifyContent="space-between">
        <QHeading size="lg">{title}</QHeading>
        <QStack direction={['column', 'row']}>
          {showRejectAndAcceptButton && (
            <QStack direction={['column', 'row']}>
              <QButton data-cy="reject-imported-event" isDestructive variant="outline" onClick={onRejectClicked}>
                {DisplayStrings.Reject}
              </QButton>
              <QButton data-cy="accept-imported-event" onClick={onAcceptClicked}>
                {DisplayStrings.Accept}
              </QButton>
            </QStack>
          )}
          {canModifyEvent && status === 'open' && (
            <QButton onClick={onEditEventClicked} data-metrics="edit-event-button" variant="outline" leftIcon="Edit3">
              {t('common.actions.edit')}
            </QButton>
          )}
          <QButton onClick={handleExport} data-metrics="export-event-button" isLoading={isExportLoading}>
            {t('events.actions.exportEvent')}
          </QButton>
          <KebabMenu
            eventStatus={status}
            eventId={eventId}
            onEditEvent={onEditEventClicked}
            onChangeOwner={onChangeOwnerClicked}
            onEscalateEvent={onEscalateEventClicked}
            onCloseEvent={onCloseEventClicked}
            onCancelEvent={onCancelEventClicked}
            onReopen={onReopenClicked}
            onOpen={onOpenClicked}
            onUpdateEventProperties={onUpdateEventPropertiesClicked}
          />
        </QStack>
      </QFlex>
      <QStack direction="row" mt={2} alignItems="baseline">
        <QText fontSize="xs">
          <StatusTag status={status} />
        </QText>
        <QText>&middot;</QText>
        <QText fontSize="xs">
          <b>{t('common.labels.due')}</b>{' '}
          {formatDate(DateTime.fromFormat(due, 'yyyy-MM-dd', { zone: 'utc' }).toString())}
        </QText>
        <QText>&middot;</QText>
        <QText fontSize="xs">
          <b>{t('common.labels.lastModified')}</b> {formatDateTime(updated_at)}
        </QText>
      </QStack>
      <QText fontSize="xs" weight="regular" color="gray.500" mt={3}>
        {description}
      </QText>
      {alertFetchingRelatedContent && (
        <QAlert
          status="warning"
          title={t('common.notifications.temporarilyUnableToRetrieveAllEventProperties')}
          description={t('common.notifications.theTeamHasBeenNotified')}
        />
      )}
    </QBox>
  );
};

export default QualityEventHeader;
